import { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { Stack, Text } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { Suspense, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';

import { PageSection } from '../../../../common/components/page-section';
import { settingsCodePlannerPath } from '../../../../routes/paths';
import { useTenantData } from '../../../../utils/tenant-context';
import { RovoDevAgentsPageHeader } from '../../components/rovo-dev-agents-page-header';
import type { codePlannerConfigurationQuery } from '../components/__generated__/codePlannerConfigurationQuery.graphql';
import query from '../components/__generated__/codePlannerConfigurationQuery.graphql';
import { CodePlannerConfiguration } from '../components/code-planner-configuration';

import { jiraProjectAdministorProjectsJql } from './types';

export const CodePlannerSettings = () => {
	const { tenantContexts } = useTenantData();
	const { cloudId } = tenantContexts[0];

	const [queryRef, loadQuery] = useQueryLoader<codePlannerConfigurationQuery>(query);

	useEffect(() => {
		if (!queryRef) {
			loadQuery({ cloudId, query: jiraProjectAdministorProjectsJql });
		}
	}, [queryRef, loadQuery, cloudId]);

	const loadingState = () => {
		return (
			<Stack alignInline="center" space="space.300">
				<Spinner size="large" />
				<Text>Loading...</Text>
			</Stack>
		);
	};

	return (
		<PageSection>
			<Stack space="space.400">
				<RovoDevAgentsPageHeader
					additionalBreadcrumbItems={[
						<BreadcrumbsItem
							href={`/dev-agents${settingsCodePlannerPath}`}
							text="Enable automatic coding plans"
							key="code-planner"
						/>,
					]}
					headingText="Enable automatic coding plans"
					descriptionText="Code Planner can automatically create coding plans for work items in a project. When this setting is enabled for a project, every new coding-related work item created will automatically have a coding plan created and added to the work item. Code Planner can still create coding plans manually, whether this setting is enabled or not. You must be a Rovo Dev Agents admin to enable automatic coding plans on a project."
				/>
				<Suspense fallback={loadingState()}>
					{queryRef && <CodePlannerConfiguration queryRef={queryRef} />}
				</Suspense>
			</Stack>
		</PageSection>
	);
};
